import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { updateProfile, changePassword, deleteAccount } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Save, Trash2, KeyRound, Loader2, User, Activity as ActivityIcon } from "lucide-react";
import { toast } from "sonner";
import ActivityPage from "@/pages/Activity";
import TwoFactorSettings from "@/components/TwoFactorSettings";

export default function UserProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [active, setActive] = useState("data");

  // Aktivitäts-Tab: Org-Rollen + Einzelnutzer (Mitarbeiter haben keinen eigenen Zugriff).
  const showActivity =
    ["steuerberater", "kanzlei_mitarbeiter", "unternehmer", "einzelnutzer"].includes(
      user?.account_type
    ) || !user?.account_type;
  const sections = [
    { key: "data", label: "Meine Daten", icon: User },
    { key: "password", label: "Sicherheit", icon: KeyRound },
    ...(showActivity ? [{ key: "activity", label: "Aktivitäten", icon: ActivityIcon }] : []),
    { key: "danger", label: "Konto löschen", icon: Trash2 },
  ];

  const [profile, setProfile] = useState({
    name: user?.name || "",
    company: user?.company || "",
    uid: user?.uid || "",
    address: user?.address || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const setP = (k) => (e) => setProfile((p) => ({ ...p, [k]: e.target.value }));
  const setPwField = (k) => (e) => setPw((p) => ({ ...p, [k]: e.target.value }));

  const saveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const updated = await updateProfile(profile);
      setUser(updated);
      toast.success("Profil gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setSavingProfile(false);
    }
  };

  const savePassword = async (e) => {
    e.preventDefault();
    if (pw.next.length < 6) {
      toast.error("Neues Passwort muss mindestens 6 Zeichen haben.");
      return;
    }
    if (pw.next !== pw.confirm) {
      toast.error("Die neuen Passwörter stimmen nicht überein.");
      return;
    }
    setSavingPw(true);
    try {
      await changePassword(pw.current, pw.next);
      setPw({ current: "", next: "", confirm: "" });
      toast.success("Passwort geändert.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Passwortänderung fehlgeschlagen.");
    } finally {
      setSavingPw(false);
    }
  };

  const handleDelete = async () => {
    try {
      await deleteAccount();
      setUser(null);
      toast.success("Konto gelöscht.");
      navigate("/login");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  return (
    <div className="max-w-3xl space-y-8" data-testid="user-profile">
      <div>
        <h1 className="font-heading text-4xl tracking-tight font-medium">Benutzer</h1>
        <p className="text-muted-foreground mt-1">Verwalten Sie Ihr Konto und Ihr Abo.</p>
      </div>

      {/* Section navigation (button group, like Einstellungen) */}
      <div className="flex flex-wrap gap-2" data-testid="user-tabs">
        {sections.map((s) => {
          const Icon = s.icon;
          const isActive = active === s.key;
          const danger = s.key === "danger";
          return (
            <Button
              key={s.key}
              variant={danger ? "destructive" : isActive ? "default" : "secondary"}
              onClick={() => setActive(s.key)}
              className={
                danger
                  ? "gap-2 border border-border bg-destructive text-destructive-foreground hover:bg-secondary hover:text-destructive"
                  : isActive
                  ? "gap-2 bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground"
                  : "gap-2 border border-border"
              }
              data-testid={`user-tab-${s.key}`}
            >
              <Icon className="h-4 w-4" />
              {s.label}
            </Button>
          );
        })}
      </div>

      {/* Meine Daten */}
      {active === "data" && (
        <form onSubmit={saveProfile} className="border border-border bg-card p-6 space-y-4" data-testid="profile-form">
          <h2 className="font-heading text-lg font-medium">Meine Daten</h2>
          <div className="space-y-2">
            <Label htmlFor="mail">E-Mail</Label>
            <Input id="mail" value={user?.email || ""} disabled className="bg-white" data-testid="profile-email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={profile.name} onChange={setP("name")} className="bg-white" data-testid="profile-name" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company">Firma</Label>
            <Input id="company" value={profile.company} onChange={setP("company")} className="bg-white" data-testid="profile-company" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="uid">UID-Nummer</Label>
            <Input id="uid" value={profile.uid} onChange={setP("uid")} className="bg-white" data-testid="profile-uid" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Adresse</Label>
            <Input id="address" value={profile.address} onChange={setP("address")} className="bg-white" data-testid="profile-address" />
          </div>
          <div className="pt-5">
            <Button type="submit" className="gap-2" disabled={savingProfile} data-testid="profile-save">
              {savingProfile ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Speichern
            </Button>
          </div>
        </form>
      )}

      {/* Passwort ändern */}
      {active === "password" && (
        <form onSubmit={savePassword} className="border border-border bg-card p-6 space-y-4" data-testid="password-form">
          <h2 className="font-heading text-lg font-medium">Passwort ändern</h2>
          <div className="space-y-2">
            <Label htmlFor="cur">Aktuelles Passwort</Label>
            <Input id="cur" type="password" value={pw.current} onChange={setPwField("current")} autoComplete="current-password" className="bg-white" data-testid="pw-current" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new">Neues Passwort</Label>
            <Input id="new" type="password" value={pw.next} onChange={setPwField("next")} autoComplete="new-password" className="bg-white" data-testid="pw-new" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="conf">Neues Passwort bestätigen</Label>
            <Input id="conf" type="password" value={pw.confirm} onChange={setPwField("confirm")} autoComplete="new-password" className="bg-white" data-testid="pw-confirm" />
          </div>
          <Button type="submit" variant="secondary" className="border border-border gap-2" disabled={savingPw} data-testid="pw-save">
            {savingPw ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
            Passwort aktualisieren
          </Button>
        </form>
      )}

      {active === "password" && <TwoFactorSettings />}

      {/* Aktivität */}
      {active === "activity" && <ActivityPage />}

      {/* Konto löschen */}
      {active === "danger" && (
        <div className="border border-destructive/40 bg-card p-6 space-y-3" data-testid="danger-zone">
          <h2 className="font-heading text-lg font-medium text-destructive">Konto löschen</h2>
          <p className="text-sm text-muted-foreground">
            Beim Löschen werden Ihr Konto und alle Ihre Belege unwiderruflich entfernt.
          </p>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" className="gap-2" data-testid="delete-account-button">
                <Trash2 className="h-4 w-4" /> Konto löschen
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Konto wirklich löschen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Diese Aktion kann nicht rückgängig gemacht werden. Alle Ihre Belege
                  werden ebenfalls gelöscht.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="delete-cancel">Abbrechen</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  data-testid="delete-confirm"
                >
                  Endgültig löschen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}
    </div>
  );
}
